import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EXPERIENCE_VALUES } from "@/lib/media";
import { CherrySwirl, CameraIcon } from "@/components/Brand";

const fieldClass = "mt-2 w-full bg-cerenza-cream/60 border border-cerenza-pink rounded-2xl px-4 h-12 text-base focus-visible:ring-2 focus-visible:ring-cerenza-cherry focus-visible:border-cerenza-cherry";

function formatDate(d) {
  if (!d) return "Selecciona una fecha";
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
}
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function BookingModal({ open, onOpenChange, defaultExperience = "" }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [experience, setExperience] = useState(defaultExperience);
  const [date, setDate] = useState();
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  useEffect(() => {
    if (open) setExperience(defaultExperience || "");
  }, [open, defaultExperience]);

  const reset = () => {
    setName(""); setEmail(""); setPhone(""); setExperience(""); setDate(undefined); setMessage("");
    setDone(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !phone || !experience || !date) {
      toast.error("Completa todos los campos para reservar tu experiencia.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name, email, phone,
        experience_type: experience,
        preferred_date: toISO(date),
        message,
      });
      setDone(true);
      toast.success("¡Solicitud recibida! Te contactamos muy pronto. 🍒");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No pudimos enviar tu reserva.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 400);
      }}
    >
      <DialogContent
        data-testid="booking-modal"
        className="max-w-2xl bg-cerenza-cream border-2 border-cerenza-pink rounded-[36px] p-0 overflow-hidden"
      >
        {!done ? (
          <div className="p-7 md:p-10 relative">
            <CherrySwirl color="#cd2e2e" className="absolute -top-3 -right-3 w-16 h-16 opacity-50" />
            <DialogHeader className="space-y-2 text-left">
              <p className="eyebrow flex items-center gap-2">
                <CameraIcon color="#cd2e2e" className="w-5 h-5" /> Reserva tu experiencia
              </p>
              <DialogTitle className="font-display text-3xl md:text-4xl text-cerenza-cherry leading-tight">
                Cuéntanos sobre tu momento.
              </DialogTitle>
              <DialogDescription className="text-cerenza-ink/65 text-base">
                Diseñamos cada experiencia de forma íntima y personalizada.
                Respondemos en menos de 24 horas.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="bk-name" className="eyebrow text-cerenza-ink/70">Nombre completo</Label>
                <Input id="bk-name" data-testid="bk-name" value={name} onChange={(e) => setName(e.target.value)} className={fieldClass} />
              </div>
              <div>
                <Label htmlFor="bk-email" className="eyebrow text-cerenza-ink/70">Email</Label>
                <Input id="bk-email" type="email" data-testid="bk-email" value={email} onChange={(e) => setEmail(e.target.value)} className={fieldClass} />
              </div>
              <div>
                <Label htmlFor="bk-phone" className="eyebrow text-cerenza-ink/70">Teléfono</Label>
                <Input id="bk-phone" data-testid="bk-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={fieldClass} />
              </div>

              <div>
                <Label className="eyebrow text-cerenza-ink/70">Tipo de experiencia</Label>
                <Select value={experience} onValueChange={setExperience}>
                  <SelectTrigger data-testid="bk-experience" className={fieldClass}>
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent className="bg-cerenza-cream border-cerenza-pink rounded-2xl">
                    {EXPERIENCE_VALUES.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="rounded-xl">{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="eyebrow text-cerenza-ink/70">Fecha preferida</Label>
                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="bk-date-trigger" className={`${fieldClass} text-left`}>
                      {formatDate(date)}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-cerenza-cream border-cerenza-pink rounded-2xl" align="start">
                    <Calendar
                      data-testid="bk-calendar"
                      mode="single"
                      selected={date}
                      onSelect={(d) => { setDate(d); if (d) setDatePopoverOpen(false); }}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="bk-message" className="eyebrow text-cerenza-ink/70">Cuéntanos tu historia (opcional)</Label>
                <Textarea id="bk-message" data-testid="bk-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-2 bg-cerenza-cream/60 border border-cerenza-pink rounded-2xl px-4 py-3 text-base focus-visible:ring-2 focus-visible:ring-cerenza-cherry focus-visible:border-cerenza-cherry" />
              </div>

              <div className="md:col-span-2 mt-1 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
                <p className="text-xs text-cerenza-ink/55">
                  Al enviar aceptas que te contactemos para coordinar tu sesión.
                </p>
                <button type="submit" data-testid="bk-submit" disabled={submitting} className="btn-cherry disabled:opacity-60">
                  {submitting ? "Enviando…" : "Reservar →"}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="p-10 md:p-14 text-center relative" data-testid="booking-success">
            <CherrySwirl color="#cd2e2e" className="absolute top-6 left-1/2 -translate-x-1/2 w-14 h-14 animate-wiggle" />
            <p className="eyebrow mt-8">— Confirmación —</p>
            <h3 className="font-display text-4xl md:text-5xl mt-3 text-cerenza-cherry leading-tight">
              Gracias, {name.split(" ")[0]}.
            </h3>
            <p className="mt-5 text-cerenza-ink/70 text-base max-w-md mx-auto leading-relaxed">
              Recibimos tu solicitud con muchísima emoción.
              Una de nuestras directoras te contactará en menos de 24 horas
              para diseñar tu experiencia.
            </p>
            <button data-testid="bk-close-success" onClick={() => onOpenChange(false)} className="btn-cherry-outline mt-8">
              Cerrar
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
